import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../Button/Button';
import { AlertIcon, MapPinIcon, RefreshIcon, ShieldIcon, type IconProps } from '../icons';

/**
 * Every dead end in the product, wearing one face.
 *
 * There are five ways a screen can fail to appear — the code threw, the
 * address does not exist, the tab is a release behind, the person is not
 * allowed in, the network did not answer — and before this they surfaced as
 * five different things, one of them React Router's developer screen. They are
 * the same event to the patient: *the page I asked for is not here*. So they
 * are one component, and the only thing that varies is the sentence and the
 * glyph.
 *
 * The tone is deliberately not alarming. Red is reserved for a genuine failure
 * (CLAUDE.md §8.2) and appears on nothing else here; a missing address is a
 * calm cyan, because nothing has gone wrong for the patient. And the copy for
 * a crash says what an anxious person actually wants to know first — that
 * their نوبت and their money are untouched.
 *
 * It never renders the error itself. A message can carry patient data
 * (CLAUDE.md §3.8); what a caller may show is a `reference`, which is the
 * correlation id the desk can read down the phone to whoever is on support.
 */
export type ErrorKind = 'crash' | 'notFound' | 'staleBuild' | 'forbidden' | 'offline';

interface ErrorPageProps {
  readonly kind: ErrorKind;
  /** Correlation id from `AppError`. Shown verbatim, so it can be read aloud. */
  readonly reference?: string | undefined;
  /** Omitted when retrying cannot help — a wrong address stays wrong. */
  readonly onRetry?: (() => void) | undefined;
  /** Where «صفحهٔ اصلی» goes: `/` on the patient app, the panel's own root on the panel. */
  readonly homeHref?: string | undefined;
}

interface Presentation {
  readonly Icon: (props: IconProps) => JSX.Element;
  /** Cyan unless something genuinely failed; see the note above. */
  readonly alarming: boolean;
}

const PRESENTATION: Record<ErrorKind, Presentation> = {
  crash: { Icon: AlertIcon, alarming: true },
  notFound: { Icon: MapPinIcon, alarming: false },
  staleBuild: { Icon: RefreshIcon, alarming: false },
  forbidden: { Icon: ShieldIcon, alarming: false },
  offline: { Icon: AlertIcon, alarming: true },
};

export function ErrorPage({ kind, reference, onRetry, homeHref }: ErrorPageProps): JSX.Element {
  const { t } = useTranslation('common');
  const { Icon, alarming } = PRESENTATION[kind];

  return (
    <div className="relative grid min-h-dvh place-items-center overflow-hidden px-5 py-14">
      {/* the same light source the rest of the app has, so this still reads as
          a page of this product rather than a browser error */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-gradient-to-b from-[var(--material-wash)] to-transparent"
      />

      <main className="page-enter relative flex w-full max-w-md flex-col items-center text-center">
        <div className="relative grid place-items-center">
          {/* a wide, soft halo — the glow is what keeps a lone glyph on an
              empty page from looking like a broken image */}
          <div
            aria-hidden="true"
            className={[
              'pointer-events-none absolute size-64 rounded-full blur-3xl',
              alarming ? 'bg-danger/20' : 'bg-primary/25',
            ].join(' ')}
          />
          {/* the plate itself: a tinted chip with a hairline of its own colour,
              which is what keeps its edge readable once the halo lights the
              page underneath it */}
          <div
            className={[
              'relative grid size-24 place-items-center rounded-full shadow-md ring-1 ring-inset',
              alarming
                ? 'bg-danger/12 text-danger ring-danger/20'
                : 'bg-primary-tint text-primary-ink ring-primary/25',
            ].join(' ')}
          >
            <Icon className="size-10" />
          </div>
        </div>

        <h1 className="type-display vibrant mt-9 text-balance">{t(`errorPage.${kind}.title`)}</h1>
        <p className="type-body mt-3 text-balance text-muted">{t(`errorPage.${kind}.body`)}</p>

        {(onRetry ?? homeHref) !== undefined ? (
          <div className="mt-9 flex w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row sm:items-center sm:justify-center">
            {onRetry === undefined ? null : (
              <Button onClick={onRetry} size="lg" variant="primary">
                {t(kind === 'staleBuild' ? 'errorPage.actions.reload' : 'actions.retry')}
              </Button>
            )}
            {homeHref === undefined ? null : (
              // a full navigation, not a client-side one: the router is exactly
              // what has just failed, so it is not the thing to ask for help
              <Button asChild size="lg" variant={onRetry === undefined ? 'primary' : 'secondary'}>
                <a href={homeHref}>{t('errorPage.actions.home')}</a>
              </Button>
            )}
          </div>
        ) : null}

        {reference === undefined ? null : (
          <p className="type-caption mt-10 text-placeholder">
            {t('errorPage.reference')}{' '}
            {/* selectable and never truncated — it exists to be read out */}
            <span className="select-all font-medium text-muted" dir="ltr">
              {reference}
            </span>
          </p>
        )}
      </main>
    </div>
  );
}
